import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Bot,
  Play,
  Mail,
  Clock,
  Calendar,
  Image,
  Settings,
  RefreshCw,
  Plus,
  Trash2,
  RotateCcw,
  SkipForward,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Save,
} from "lucide-react";

const TIMEZONES = [
  "America/Toronto",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Vancouver",
  "UTC",
  "Europe/London",
  "Europe/Paris",
];

const CATEGORIES = [
  { value: "collective-agreements", label: "Collective Agreements" },
  { value: "contract-interpretation", label: "Contract Interpretation" },
  { value: "labour-law-basics", label: "Labour Law Basics" },
  { value: "hr-compliance", label: "HR & Compliance" },
  { value: "arbitration-grievances", label: "Arbitration & Grievances" },
  { value: "guides-explainers", label: "Guides & Explainers" },
  { value: "case-examples", label: "Case Examples" },
];

export default function AdminAIBlogSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Action states
  const [runningGeneration, setRunningGeneration] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Topic dialog state
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopic, setNewTopic] = useState({
    topic: "",
    primary_keyword: "",
    secondary_keywords: "",
    categories: [],
    priority: 3,
  });
  
  // Topics list
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  
  // Guidance dialog
  const [showGuidance, setShowGuidance] = useState(false);
  const [guidance, setGuidance] = useState(null);

  const fetchSettings = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/blog/ai/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to load AI settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTopics = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    setTopicsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/blog/ai/topics?page_size=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTopics(response.data.topics || []);
    } catch (error) {
      console.error(error);
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  const fetchGuidance = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/blog/ai/guidance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuidance(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchTopics();
    fetchGuidance();
  }, [fetchSettings, fetchTopics, fetchGuidance]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/blog/ai/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
      setHasChanges(false);
      toast.success("Settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    const token = localStorage.getItem("admin_token");
    setRunningGeneration(true);
    try {
      const response = await axios.post(
        `${API}/admin/blog/ai/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Generated: ${response.data.title}`);
        fetchSettings();
      } else {
        toast.error(response.data.error || "Generation failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setRunningGeneration(false);
    }
  };

  const handleSendTestEmail = async () => {
    const token = localStorage.getItem("admin_token");
    setSendingTestEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/blog/ai/send-test-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`Test email sent to ${response.data.sent_to}`);
      } else {
        toast.error("Failed to send test email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleAddTopic = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(
        `${API}/admin/blog/ai/topics`,
        {
          topic: newTopic.topic,
          primary_keyword: newTopic.primary_keyword,
          secondary_keywords: newTopic.secondary_keywords.split(",").map((s) => s.trim()).filter(Boolean),
          categories: newTopic.categories,
          priority: newTopic.priority,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Topic added");
      setShowAddTopic(false);
      setNewTopic({ topic: "", primary_keyword: "", secondary_keywords: "", categories: [], priority: 3 });
      fetchTopics();
      fetchSettings();
    } catch (error) {
      toast.error("Failed to add topic");
    }
  };

  const handleResetUsedTopics = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.post(
        `${API}/admin/blog/ai/topics/reset-used`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reset ${response.data.reset_count} topics`);
      fetchTopics();
      fetchSettings();
    } catch (error) {
      toast.error("Failed to reset topics");
    }
  };

  const handleSkipNext = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.post(
        `${API}/admin/blog/ai/topics/skip-next`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Skipped: ${response.data.skipped_topic}`);
      fetchTopics();
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to skip topic");
    }
  };

  const handleResetGuidance = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(
        `${API}/admin/blog/ai/guidance/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Guidance reset to defaults");
      fetchGuidance();
      fetchSettings();
    } catch (error) {
      toast.error("Failed to reset guidance");
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-blog-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bot className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-heading font-semibold">AI Blog Settings</h1>
            <p className="text-muted-foreground">Configure automatic blog generation</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="enabled">AI Generation Enabled</Label>
              <Switch
                id="enabled"
                checked={settings?.enabled}
                onCheckedChange={(v) => updateSetting("enabled", v)}
                data-testid="enabled-switch"
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Run:</span>
                <span>{formatDateTime(settings?.last_run_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <span className="flex items-center gap-1">
                  {settings?.last_run_status === "ok" && <CheckCircle className="w-4 h-4 text-green-500" />}
                  {settings?.last_run_status === "error" && <XCircle className="w-4 h-4 text-red-500" />}
                  {settings?.last_run_status || "Never run"}
                </span>
              </div>
              {settings?.last_error && (
                <div className="text-red-600 text-xs bg-red-50 p-2 rounded">
                  {settings.last_error}
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next Run:</span>
                <span className="font-medium">{formatDateTime(settings?.next_run_at)}</span>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRunNow}
                disabled={runningGeneration}
                data-testid="run-now-btn"
              >
                <Play className="w-4 h-4 mr-1" />
                {runningGeneration ? "Running..." : "Run Now"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
                data-testid="test-email-btn"
              >
                <Mail className="w-4 h-4 mr-1" />
                {sendingTestEmail ? "Sending..." : "Test Email"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Schedule Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <Select
                value={settings?.frequency}
                onValueChange={(v) => updateSetting("frequency", v)}
              >
                <SelectTrigger data-testid="frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekdays">Weekdays Only</SelectItem>
                  <SelectItem value="twice_weekly">Twice Weekly (Tue & Fri)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Run Time</Label>
                <div className="flex gap-1">
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={settings?.run_time_hour}
                    onChange={(e) => updateSetting("run_time_hour", parseInt(e.target.value) || 0)}
                    className="w-16"
                    data-testid="run-hour-input"
                  />
                  <span className="self-center">:</span>
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    value={settings?.run_time_minute}
                    onChange={(e) => updateSetting("run_time_minute", parseInt(e.target.value) || 0)}
                    className="w-16"
                    data-testid="run-minute-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select
                  value={settings?.timezone}
                  onValueChange={(v) => updateSetting("timezone", v)}
                >
                  <SelectTrigger data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approval Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Approval
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Approval Email</Label>
              <Input
                type="email"
                value={settings?.approval_email || ""}
                onChange={(e) => updateSetting("approval_email", e.target.value)}
                placeholder="basel@cbaiq.com"
                data-testid="approval-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Token Expiry (hours)</Label>
              <Input
                type="number"
                min={24}
                max={168}
                value={settings?.token_ttl_hours}
                onChange={(e) => updateSetting("token_ttl_hours", parseInt(e.target.value) || 72)}
                data-testid="token-ttl-input"
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <Label>Enable scheduled default</Label>
              <Switch
                checked={settings?.schedule_default_enabled}
                onCheckedChange={(v) => updateSetting("schedule_default_enabled", v)}
              />
            </div>
            
            {settings?.schedule_default_enabled && (
              <div className="flex gap-2 items-center">
                <Label className="text-sm text-muted-foreground">Schedule for:</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={settings?.schedule_default_time_hour}
                  onChange={(e) => updateSetting("schedule_default_time_hour", parseInt(e.target.value) || 9)}
                  className="w-16"
                />
                <span>:</span>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={settings?.schedule_default_time_minute}
                  onChange={(e) => updateSetting("schedule_default_time_minute", parseInt(e.target.value) || 0)}
                  className="w-16"
                />
                <span className="text-sm text-muted-foreground">next day</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Generation Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Content Generation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Posts per Run</Label>
                <Select
                  value={String(settings?.posts_per_run)}
                  onValueChange={(v) => updateSetting("posts_per_run", parseInt(v))}
                >
                  <SelectTrigger data-testid="posts-per-run-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 post</SelectItem>
                    <SelectItem value="2">2 posts</SelectItem>
                    <SelectItem value="3">3 posts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Max Word Count</Label>
                <Input
                  type="number"
                  min={400}
                  max={2000}
                  value={settings?.max_word_count}
                  onChange={(e) => updateSetting("max_word_count", parseInt(e.target.value) || 1200)}
                  data-testid="max-words-input"
                />
              </div>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Draft First (Recommended)</Label>
                <p className="text-xs text-muted-foreground">Always create as draft, require approval</p>
              </div>
              <Switch
                checked={settings?.draft_first}
                onCheckedChange={(v) => updateSetting("draft_first", v)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Auto-Publish Mode</Label>
              <Select
                value={settings?.auto_publish_mode}
                onValueChange={(v) => updateSetting("auto_publish_mode", v)}
              >
                <SelectTrigger data-testid="auto-publish-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approval_required">Always Require Approval</SelectItem>
                  <SelectItem value="if_no_risk_flags">Auto-publish if No Risk Flags</SelectItem>
                  <SelectItem value="always">
                    <span className="text-red-600">⚠️ Always Auto-publish (Danger)</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Image Generation Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Image Generation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Generate Featured Images</Label>
              <Switch
                checked={settings?.image_enabled}
                onCheckedChange={(v) => updateSetting("image_enabled", v)}
                data-testid="image-enabled-switch"
              />
            </div>
            
            {settings?.image_enabled && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Generate After Approval Only</Label>
                    <p className="text-xs text-muted-foreground">Saves cost by waiting for approval</p>
                  </div>
                  <Switch
                    checked={settings?.image_after_approval_only}
                    onCheckedChange={(v) => updateSetting("image_after_approval_only", v)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Aspect Ratio</Label>
                  <Select
                    value={settings?.image_aspect_ratio}
                    onValueChange={(v) => updateSetting("image_aspect_ratio", v)}
                  >
                    <SelectTrigger data-testid="aspect-ratio-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16:9">16:9 (Landscape)</SelectItem>
                      <SelectItem value="1:1">1:1 (Square)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Drift & Guidance Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Drift & Guidance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Prompt Version:</span>
                <Badge variant="outline">{settings?.prompt_version}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Guidance Version:</span>
                <Badge variant="outline">{settings?.guidance_version}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Banned Phrases:</span>
                <span>{settings?.guidance_stats?.banned_phrases_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Required Softeners:</span>
                <span>{settings?.guidance_stats?.required_softeners_count || 0}</span>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex gap-2">
              <Dialog open={showGuidance} onOpenChange={setShowGuidance}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">View Guidance</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Current Prompt Guidance</DialogTitle>
                    <DialogDescription>
                      Learned patterns from feedback (v{guidance?.version || 1})
                    </DialogDescription>
                  </DialogHeader>
                  {guidance && (
                    <div className="space-y-4 text-sm">
                      <div>
                        <h4 className="font-medium mb-2">Banned Phrases</h4>
                        <div className="flex flex-wrap gap-1">
                          {guidance.banned_phrases?.map((p, i) => (
                            <Badge key={i} variant="destructive" className="text-xs">{p}</Badge>
                          ))}
                          {!guidance.banned_phrases?.length && <span className="text-muted-foreground">None</span>}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Required Softeners</h4>
                        <div className="flex flex-wrap gap-1">
                          {guidance.required_softeners?.map((s, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Tone Notes</h4>
                        <ul className="list-disc list-inside text-muted-foreground">
                          {guidance.tone_notes?.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">Reset Guidance</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset Guidance?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will reset all learned patterns to defaults. All banned phrases, tone notes, and examples will be cleared.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleResetGuidance} className="bg-destructive">
                      Reset
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Topic Queue Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Topic Queue
            </CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex gap-2 text-sm">
                <Badge variant="secondary">{settings?.topic_queue_stats?.queued || 0} Queued</Badge>
                <Badge variant="outline">{settings?.topic_queue_stats?.used || 0} Used</Badge>
                <Badge variant="outline">{settings?.topic_queue_stats?.skipped || 0} Skipped</Badge>
              </div>
              <div className="flex gap-2">
                <Dialog open={showAddTopic} onOpenChange={setShowAddTopic}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-topic-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Topic
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Topic</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Topic *</Label>
                        <Textarea
                          value={newTopic.topic}
                          onChange={(e) => setNewTopic({ ...newTopic, topic: e.target.value })}
                          placeholder="Understanding seniority clauses in collective agreements"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Primary Keyword *</Label>
                        <Input
                          value={newTopic.primary_keyword}
                          onChange={(e) => setNewTopic({ ...newTopic, primary_keyword: e.target.value })}
                          placeholder="seniority clause"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Secondary Keywords (comma-separated)</Label>
                        <Input
                          value={newTopic.secondary_keywords}
                          onChange={(e) => setNewTopic({ ...newTopic, secondary_keywords: e.target.value })}
                          placeholder="layoff order, bumping rights, years of service"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Priority (1-5)</Label>
                        <Select
                          value={String(newTopic.priority)}
                          onValueChange={(v) => setNewTopic({ ...newTopic, priority: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((p) => (
                              <SelectItem key={p} value={String(p)}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleAddTopic} disabled={!newTopic.topic || !newTopic.primary_keyword}>
                        Add Topic
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Button variant="outline" size="sm" onClick={handleSkipNext}>
                  <SkipForward className="w-4 h-4 mr-1" />
                  Skip Next
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset Used
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset Used Topics?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will move all "used" topics back to "queued" status, allowing them to be used again.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleResetUsedTopics}>Reset</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {topicsLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {topics.filter(t => t.status === "queued").slice(0, 10).map((topic, idx) => (
                <div key={topic.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                    <span className="truncate max-w-md">{topic.topic}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">P{topic.priority}</Badge>
                    <Badge variant="outline" className="text-xs">{topic.primary_keyword}</Badge>
                  </div>
                </div>
              ))}
              {topics.filter(t => t.status === "queued").length === 0 && (
                <p className="text-center text-muted-foreground py-4">No queued topics</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
